import type {
  PacketSelector,
  SimpleTest,
} from '@ipfabric/utils/pathLookup/Security';

export interface PacketSelectorTranslations {
  icmp: {
    [Key in (PacketSelector & { header: 'icmp' })['field']]: string;
  };
  icmpv6: {
    [Key in (PacketSelector & { header: 'icmpv6' })['field']]: string;
  };
  interface: {
    [Key in (PacketSelector & {
      header: 'interface';
    })['field']]: string;
  };
  ip: {
    [Key in (PacketSelector & { header: 'ip' })['field']]: string;
  };
  ipv6: {
    [Key in (PacketSelector & { header: 'ipv6' })['field']]: string;
  };
  l4: {
    [Key in (PacketSelector & { header: 'l4' })['field']]: string;
  };
  l7: {
    [Key in (PacketSelector & { header: 'l7' })['field']]: string;
  };
  options: {
    [Key in (PacketSelector & { header: 'options' })['field']]: string;
  };
  processing: {
    [Key in (PacketSelector & {
      header: 'processing';
    })['field']]: string;
  };
  tcp: {
    [Key in (PacketSelector & { header: 'tcp' })['field']]: string;
  };
  udp: {
    [Key in (PacketSelector & { header: 'udp' })['field']]: string;
  };
  eth: {
    [Key in (PacketSelector & { header: 'eth' })['field']]: string;
  };
  vxlan: {
    [Key in (PacketSelector & { header: 'vxlan' })['field']]: string;
  };
  mpls: {
    [Key in (PacketSelector & { header: 'mpls' })['field']]: string;
  };
  natPreRouting: {
    [Key in (PacketSelector & { header: 'natPreRouting' })['field']]: string;
  };
  routing: {
    [Key in (PacketSelector & { header: 'routing' })['field']]: string;
  };
}

const packetSelector: PacketSelectorTranslations = {
  icmp: {
    type: 'ICMP type',
    code: 'ICMP code',
  },
  icmpv6: {
    type: 'ICMPv6 type',
    code: 'ICMPv6 code',
  },
  interface: {
    in: 'Input interface',
    out: 'Output interface',
    inZone: 'Input zone',
    outZone: 'Output zone',
    intraZone: 'Traffic type intrazone',
    interZone: 'Traffic type interzone',
    vrf: 'VRF',
    toHostVmNicId: 'Input interface',
    fromHostVmNicId: 'Output interface',
  },
  ip: {
    src: 'IP source address',
    dst: 'IP destination address',
    dscp: 'IP DSCP',
    dstRegion: 'IP destination region',
    dstFqdn: 'FQDN destination',
    srcFqdn: 'FQDN source',
    dstThreatIntelligence: 'Threat intelligence destination',
    srcThreatIntelligence: 'Threat intelligence source',
    fragmentOffset: 'IP fragment offset',
    'packet-length': 'IP packet length',
    precedence: 'IP precedence',
    protocol: 'IP protocol',
    srcRegion: 'IP source region',
    tos: 'IP type of service',
    ttl: 'IP time to live',
  },
  ipv6: {
    src: 'IPv6 source address',
    dst: 'IPv6 destination address',
    hopLimit: 'IP time to live',
  },
  l4: {
    src: 'Source Port',
    dst: 'Destination Port',
  },
  l7: {
    application: 'Application',
    category: 'Category', // TODO: NIM-16643 extend UI form with Category field
    url: 'URL Filter', // TODO: NIM-16643: extend UI form with URL / Domain field
  },
  options: {
    tracked: 'Tracked option',
  },
  processing: {
    phase: 'Processing phase',
  },
  tcp: {
    src: 'TCP source port',
    dst: 'TCP destination port',
    flags: 'TCP flags',
  },
  udp: {
    src: 'UDP source port',
    dst: 'UDP destination port',
  },
  eth: {
    src: 'MAC source address',
    dst: 'MAC destination address',
    vlan: 'VLAN',
  },
  vxlan: {
    srcGrp: 'VXLAN source group',
    dstGrp: 'VXLAN destination group',
    vni: 'VXLAN VNI',
    intraGrp: 'Intra group',
    interGrp: 'Inter group',
  },
  mpls: {
    labelIn: 'MPLS Input Label',
  },
  natPreRouting: {
    chainName: 'NAT Pre-Routing Chain Name',
    ruleName: 'NAT Pre-Routing Rule Name',
  },
  routing: {
    sdWanProtocol: 'SD-WAN / VPN Protocol',
  },
};

type OperatorTranslations = {
  [Key in SimpleTest['operator']]: string;
};

const comparisons = {
  eq: 'Is equal to',
  notEq: 'Is not equal to',
  oneOf: 'Is one of',
  lt: 'Is less than',
  lte: 'Is less than or equal',
  gt: 'Is greater than',
  gte: 'Is greater than or equal',
  isMatching: 'Is matching',
  matches: 'Matches',
};

const operator: OperatorTranslations = {
  'arr-contains-all-str': 'Contains all',
  'arr-contains-any-str': 'Contains any',
  'arr-contains-str': 'Contains',
  'boolean==': comparisons.eq,
  false: 'FALSE',
  'num!=': comparisons.notEq,
  'num-and-eq': comparisons.matches,
  'num-in-arr': comparisons.oneOf,
  'num-in-set': comparisons.oneOf,
  'num<': comparisons.lt,
  'num<=': comparisons.lte,
  'num==': comparisons.eq,
  'num>': comparisons.gt,
  'num>=': comparisons.gte,
  'numset!=': comparisons.notEq,
  'numset-in-arr': comparisons.oneOf,
  'numset-matches': 'Matches',
  'numset-not-matches': comparisons.notEq,
  'numset-and-eq': 'Matches',
  'numset<': comparisons.lt,
  'numset<=': comparisons.lte,
  'numset==': comparisons.eq,
  'numset>': comparisons.gt,
  'numset>=': comparisons.gte,
  'rx-matching-str': comparisons.isMatching, // TODO: NIM-16643
  'rx-matching-str-arr': comparisons.isMatching, // TODO: NIM-16643
  'str!=': comparisons.notEq,
  'str-in-arr': comparisons.oneOf,
  'str-matches-mac': comparisons.matches,
  'str-matches-rx': comparisons.matches, // TODO: NIM-16643
  'str-matches-rx-arr': comparisons.matches, // TODO: NIM-16643
  'str==': comparisons.eq,
  'strset!=': comparisons.notEq,
  'strset-in-arr': comparisons.oneOf,
  'strset-matches': comparisons.matches,
  'strset<': comparisons.lt,
  'strset<=': comparisons.lte,
  'strset==': comparisons.eq,
  'strset>': comparisons.gt,
  'strset>=': comparisons.gte,
  'strset-not-matches': comparisons.notEq,
  'strset-and-eq': 'Matches',
  true: 'TRUE',
};

export const securitySchemaMessages = {
  en: {
    breadcrumbs: {
      scopeType: {
        acl: 'ACL',
        zoneFw: 'Zone firewall',
        referencedTest: 'Test',
        pbr: 'Policy based routing',
        nat44: 'NAT44',
        cloud: 'Cloud security',
      },
      scopeTypeShort: {
        acl: 'ACL',
        zoneFw: 'Zone FW',
        pbr: 'PBR',
        nat44: 'NAT44',
        cloud: 'Cloud',
      },
    },
    chainNotFound: 'Specified rule chain was not found.',
    entityList: {
      title: {
        acl: 'ACL rule chains',
        zoneFw: 'Zone firewall policies',
        pbr: 'Policy based routing',
        nat44: 'NAT44',
        cloud: 'Cloud security policies',
      },
      empty: 'empty',
      emptyFiltered: 'No matching results',
    },
    loadError: 'Failed to load the security tree.',
    noScope: 'No scope specified to be displayed.',
    noSecurityForHost: 'No security data are available for the host.',
    referencedTestNotFound: 'Specified referenced test was not found.',
    rule: {
      action: 'Action',
      priority: 'Priority',
      status: {
        label: 'Status',
        active: 'Active',
        inactive: 'Inactive',
      },
      labelFromNumber: (num: number) => `Rule #${num}`,
    },
    ruleChain: {
      defaultAction: 'Default action',
      defaultHighlightedRuleLabel: 'Highlighted rule',

      nameTooltip:
        'A chain of rules that define given security policy. You can expand the rule boxes to inspect the details of the individual rules.',
    },
    numberOfItems: '{number, plural, one {# item} other {# items}}',
    test: {
      groupLabels: {
        and: 'AND',
        not: 'NOT',
        or: 'OR',
      },
      metaInfo: {
        name: 'Name',
        category: 'Category',
        reference: 'Ref',
      },
      error: {
        label: 'Error',
        circularRecursionReference:
          'Reference already visited in one of ancestor nodes',
        circularReference: 'Circular reference',
        notFoundReference: 'Test not found',
        unknownNode: 'Invalid security schema data',
        unresolvedReference: 'Reference not resolved due to unknown error',
      },
      simple: {
        booleanFalse: 'NOT',
        packetSelector,
        operator,
        value: {
          boolean: {
            true: 'true',
            false: 'false',
          },
          error: 'Value error',
        },
      },
      eval: {
        chainType: {
          acl: 'ACL',
          zoneFw: 'Zone firewall',
          pbr: 'Policy based routing',
          nat44: 'NAT44',
          cloud: 'Cloud security',
        },
        checkType: {
          passing: 'Passing',
          failing: 'Failing',
        },
      },
    },
    unknown: {
      label: 'Unknown',
      explanation: 'Unsupported data type in security schema',
    },
    pathLookupOptions: {
      definedInRouting: 'defined in routing',
    },
  },
} as const;
